import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useCreateSupportCase } from '../../../data/support/useSupport';
import { SupportCasePriority } from '../../../types/support.types';

interface CreateCaseDialogProps {
  open: boolean;
  onClose: () => void;
}

const PRIORITY_OPTIONS: SupportCasePriority[] = [
  'low',
  'medium',
  'high',
  'critical',
];

const CreateCaseDialog: React.FC<CreateCaseDialogProps> = ({
  open,
  onClose,
}) => {
  const [accountId, setAccountId] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [priority, setPriority] = React.useState<SupportCasePriority>('medium');
  const [assigneeId, setAssigneeId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const createCase = useCreateSupportCase();

  const handleSubmit = () => {
    setError(null);
    createCase.mutate(
      {
        account_id: accountId,
        subject,
        description,
        priority,
        assignee_user_id: assigneeId || undefined,
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
        },
        onError: (err: any) => {
          const detail = err?.response?.data?.detail || 'Failed to create case';
          setError(detail);
        },
      },
    );
  };

  const resetForm = () => {
    setAccountId('');
    setSubject('');
    setDescription('');
    setPriority('medium');
    setAssigneeId('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Case</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          label="Account ID"
          fullWidth
          size="small"
          required
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          label="Subject"
          fullWidth
          size="small"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Description"
          fullWidth
          size="small"
          required
          multiline
          minRows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Priority"
          fullWidth
          size="small"
          select
          value={priority}
          onChange={(e) => setPriority(e.target.value as SupportCasePriority)}
          sx={{ mb: 2 }}
        >
          {PRIORITY_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Assignee ID"
          helperText="Optional — leave blank for unassigned"
          fullWidth
          size="small"
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            !accountId || !subject || !description || createCase.isPending
          }
        >
          {createCase.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCaseDialog;
